import React, { useState } from 'react';
import { Form, Input, Switch, Button, Card, Typography, Row, Col, message, Divider, InputNumber } from 'antd';
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SystemSettings = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    // 基本设置
    siteName: 'ClipAI Note System',
    siteDescription: 'Intelligent Note Management System',
    adminEmail: 'admin@example.com',
    
    // 功能设置
    enableRegistration: true,
    enableEmailVerification: true,
    enableGoogleAuth: true,
    enablePasswordReset: true,
    
    // 限制设置
    maxNotesPerUser: 1000,
    maxFileSize: 10, // MB
    sessionTimeout: 24, // 小时
    
    // 邮件设置
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    enableSmtpSSL: true,
    
    // 安全设置
    enableTwoFactor: false,
    passwordMinLength: 8,
    enablePasswordComplexity: true,
    maxLoginAttempts: 5,
    lockoutDuration: 30, // 分钟
    
    // 系统维护
    maintenanceMode: false,
    maintenanceMessage: 'System is under maintenance, please try again later.',
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // 这里应该调用API保存设置
      console.log('保存设置:', values);
      
      // 模拟API调用延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      message.success('Settings saved successfully');
    } catch (error) {
      message.error('Please check the form input');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.setFieldsValue(initialValues);
    message.info('Settings have been reset to defaults');
  };

  return (
    <div className="admin-module">
      <div className="admin-module-header">
        <Title level={3} className="admin-module-title">System Settings</Title>
        <Text className="admin-module-description">
          Configure system parameters and feature toggles
        </Text>
      </div>

      <Form
        form={form}
        layout="vertical"
        initialValues={initialValues}
        onFinish={handleSave}
      >
        <Row gutter={[24, 24]}>
          <Col xs={24} lg={12}>
            <Card title="Basic Settings" size="small">
              <Form.Item
                name="siteName"
                label="Site Name"
                rules={[{ required: true, message: 'Please enter site name!' }]}
              >
                <Input placeholder="Enter site name" />
              </Form.Item>
              
              <Form.Item
                name="siteDescription"
                label="Site Description"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter site description"
                />
              </Form.Item>
              
              <Form.Item
                name="adminEmail"
                label="Admin Email"
                rules={[
                  { required: true, message: 'Please enter admin email!' },
                  { type: 'email', message: 'Please enter a valid email address!' }
                ]}
              >
                <Input placeholder="Enter admin email" />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Feature Settings" size="small">
              <Form.Item
                name="enableRegistration"
                label="Allow User Registration"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="enableEmailVerification"
                label="Email Verification"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="enableGoogleAuth"
                label="Google Login"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="enablePasswordReset"
                label="Password Reset"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Limit Settings" size="small">
              <Form.Item
                name="maxNotesPerUser"
                label="Max Notes Per User"
                rules={[{ required: true, message: 'Please enter max notes!' }]}
              >
                <InputNumber
                  min={1}
                  max={10000}
                  style={{ width: '100%' }}
                  placeholder="Enter max notes"
                />
              </Form.Item>
              
              <Form.Item
                name="maxFileSize"
                label="Max File Size (MB)"
                rules={[{ required: true, message: 'Please enter max file size!' }]}
              >
                <InputNumber
                  min={1}
                  max={100}
                  style={{ width: '100%' }}
                  placeholder="Enter max file size"
                />
              </Form.Item>
              
              <Form.Item
                name="sessionTimeout"
                label="Session Timeout (Hours)"
                rules={[{ required: true, message: 'Please enter session timeout!' }]}
              >
                <InputNumber
                  min={1}
                  max={168}
                  style={{ width: '100%' }}
                  placeholder="Enter session timeout"
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Email Settings" size="small">
              <Form.Item
                name="smtpHost"
                label="SMTP Server"
                rules={[{ required: true, message: 'Please enter SMTP server!' }]}
              >
                <Input placeholder="smtp.gmail.com" />
              </Form.Item>
              
              <Form.Item
                name="smtpPort"
                label="SMTP Port"
                rules={[{ required: true, message: 'Please enter SMTP port!' }]}
              >
                <InputNumber
                  min={1}
                  max={65535}
                  style={{ width: '100%' }}
                  placeholder="587"
                />
              </Form.Item>
              
              <Form.Item
                name="smtpUser"
                label="SMTP Username"
              >
                <Input placeholder="Enter SMTP username" />
              </Form.Item>
              
              <Form.Item
                name="smtpPassword"
                label="SMTP Password"
              >
                <Input.Password placeholder="Enter SMTP password" />
              </Form.Item>
              
              <Form.Item
                name="enableSmtpSSL"
                label="Enable SSL"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="Security Settings" size="small">
              <Form.Item
                name="enableTwoFactor"
                label="Two-Factor Authentication"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="passwordMinLength"
                label="Minimum Password Length"
                rules={[{ required: true, message: 'Please enter minimum password length!' }]}
              >
                <InputNumber
                  min={6}
                  max={20}
                  style={{ width: '100%' }}
                  placeholder="8"
                />
              </Form.Item>
              
              <Form.Item
                name="enablePasswordComplexity"
                label="Password Complexity Requirements"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="maxLoginAttempts"
                label="Max Login Attempts"
              >
                <InputNumber
                  min={3}
                  max={10}
                  style={{ width: '100%' }}
                  placeholder="5"
                />
              </Form.Item>
              
              <Form.Item
                name="lockoutDuration"
                label="Lockout Duration (Minutes)"
              >
                <InputNumber
                  min={5}
                  max={120}
                  style={{ width: '100%' }}
                  placeholder="30"
                />
              </Form.Item>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card title="System Maintenance" size="small">
              <Form.Item
                name="maintenanceMode"
                label="Maintenance Mode"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              
              <Form.Item
                name="maintenanceMessage"
                label="Maintenance Message"
              >
                <TextArea
                  rows={3}
                  placeholder="Enter maintenance message"
                />
              </Form.Item>
            </Card>
          </Col>
        </Row>

        <Divider />

        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={handleSave}
            size="large"
            style={{ marginRight: 16 }}
          >
            Save Settings
          </Button>
          <Button
            icon={<ReloadOutlined />}
            onClick={handleReset}
            size="large"
          >
            Reset to Defaults
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default SystemSettings; 